import type { Place } from '@wingman/shared';

/**
 * Live signals derived strictly from real Place fields. There is no trend or
 * popularity history in the data model, so we surface only what the data
 * honestly supports: how fresh the listing is, how much review confidence
 * backs the rating, and whether the place is about to close. Empty states are
 * shown rather than fabricated numbers.
 */

const DAY_ORDER = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/** Human relative time from an ISO timestamp ("3d ago", "2h ago"). */
export function timeAgo(iso: string | undefined): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'just now';
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

export interface FreshnessSignal {
  label: string;
  detail: string | null;
  tone: 'green' | 'amber' | 'red' | 'muted';
}

/** Listing-freshness signal from freshnessStatus + lastCheckedAt. */
export function getFreshnessSignal(place: Place): FreshnessSignal | null {
  const status = place.freshnessStatus;
  if (!status) return null;
  const checked = timeAgo(place.lastCheckedAt);

  switch (status) {
    case 'verified':
      return {
        label: 'Verified',
        detail: checked ? `Checked ${checked}` : 'Recently confirmed',
        tone: 'green',
      };
    case 'aging':
      return {
        label: 'Aging',
        detail: checked ? `Last checked ${checked}` : 'Not recently confirmed',
        tone: 'amber',
      };
    case 'stale':
      return {
        label: 'Stale',
        detail: checked ? `Last checked ${checked}` : 'Needs re-verification',
        tone: 'red',
      };
    case 'reported':
      return { label: 'Reported', detail: 'Flagged for verification', tone: 'red' };
    case 'quarantined':
      return { label: 'Quarantined', detail: 'Under review', tone: 'red' };
    default:
      return null;
  }
}

export interface ConfidenceSignal {
  label: string;
  tone: 'green' | 'amber' | 'muted';
}

/**
 * How much the rating is backed by real reviews. Honest tiers — a 4.8 from 2
 * reviews is "New", not "Top rated". Returns null when there's no rating.
 */
export function getConfidenceSignal(place: Place): ConfidenceSignal | null {
  if (place.rating === undefined || place.rating <= 0) return null;
  const count = place.reviewCount ?? 0;
  if (count >= 50) return { label: 'High confidence', tone: 'green' };
  if (count >= 10) return { label: 'Solid signal', tone: 'green' };
  if (count >= 3) return { label: 'Building', tone: 'amber' };
  if (count >= 1) return { label: 'Early reviews', tone: 'amber' };
  return { label: 'No reviews yet', tone: 'muted' };
}

/** Parse an "HH:MM-HH:MM" or "HH:MM" hours string into minutes-of-day. */
function parseCloseMinutes(hoursValue: string): number | null {
  // Take the last time token (handles "09:00-22:00", "09:00–22:00", "9am-10pm" is not supported — OSM uses 24h).
  const parts = hoursValue.split(/[-–]/).map((p) => p.trim());
  const close = parts[parts.length - 1];
  const m = close?.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

/**
 * If the place is open and closes within `windowMin`, returns minutes until
 * close. Returns null otherwise. Uses the real `hours` field for today.
 */
export function getClosingSoon(place: Place, windowMin = 60): number | null {
  if (!place.hours) return null;
  const today = DAY_ORDER[new Date().getDay()];
  const todayHours = place.hours[today];
  if (!todayHours || /closed|off/i.test(todayHours)) return null;

  const closeMin = parseCloseMinutes(todayHours);
  if (closeMin === null) return null;

  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const diff = closeMin - nowMin;
  if (diff > 0 && diff <= windowMin) return diff;
  return null;
}
